<script setup lang="ts">
import CopyId from '@/components/ui/CopyId.vue'
import { ref, computed, watch, nextTick } from 'vue'
import { useRouter } from 'vue-router'
import { useQuery } from '@tanstack/vue-query'
import { Search, FolderKanban, Package, Tag } from 'lucide-vue-next'
import { api } from '@/lib/api'

const emit = defineEmits<{ close: [] }>()
const router = useRouter()
const query = ref('')
const activeIndex = ref(0)
const inputRef = ref<HTMLInputElement | null>(null)

const { data: projects } = useQuery({
  queryKey: ['projects', {}],
  queryFn: () => api.projects.list(),
  staleTime: 60_000,
})

const { data: pos } = useQuery({
  queryKey: ['pos', {}],
  queryFn: () => api.procurement.pos(),
  staleTime: 60_000,
})

// Tracked-item / serial search (M4 G1, docs/specs/warehouse-m4-serials-rma-packing.md
// "Serial numbers": "Staff Cmd-K search ... include serial"). Server-side, unlike the
// projects/POs lists above — the tracked-items table is far larger than what's worth holding
// in memory for every Cmd-K open — so this only fires once the query is long enough to be
// worth a round trip.
const itemSearchQuery = computed(() => query.value.trim())
const { data: itemMatches } = useQuery({
  queryKey: ['command-palette', 'items', itemSearchQuery],
  queryFn: () => api.warehouse.listItems({ search: itemSearchQuery.value }),
  enabled: computed(() => itemSearchQuery.value.length >= 2),
  staleTime: 10_000,
})

interface Result {
  id: string
  label: string
  // When set, label starts with this ID (SKY- / PO number); it renders with a copy button.
  copyId?: string
  sub: string
  type: 'project' | 'po' | 'item'
  route: { name: string; params?: Record<string, string>; query?: Record<string, string> }
}

const results = computed<Result[]>(() => {
  const q = query.value.trim().toLowerCase()
  if (!q) return []

  const out: Result[] = []

  for (const p of projects.value ?? []) {
    if (
      p.code.toLowerCase().includes(q) ||
      p.name.toLowerCase().includes(q) ||
      (p.account?.name ?? '').toLowerCase().includes(q)
    ) {
      out.push({
        id: p.id,
        label: `${p.code} — ${p.name}`,
        sub: `${p.status} · ${p.account?.name ?? ''}`,
        type: 'project',
        route: { name: 'project', params: { id: p.id } },
      })
    }
    if (out.length >= 8) break
  }

  for (const po of pos.value ?? []) {
    if (
      po.poNumber.toLowerCase().includes(q) ||
      (po.vendor?.name ?? '').toLowerCase().includes(q) ||
      (po.project?.code ?? '').toLowerCase().includes(q)
    ) {
      out.push({
        id: po.id,
        label: `${po.poNumber} — ${po.vendor?.name ?? 'Skylark stock'}`,
        copyId: po.poNumber,
        sub: `PO · ${po.project?.code ?? ''} ${po.project?.name ?? ''}`,
        type: 'po',
        route: { name: 'project', params: { id: po.project?.id ?? '' }, query: { tab: 'pos' } },
      })
    }
    if (out.length >= 12) break
  }

  // Tracked items — already filtered server-side (GET /warehouse/items?search=, which matches
  // skylarkId/description/serialNumber/manufacturer/model), so no client-side re-filter here.
  for (const item of itemMatches.value ?? []) {
    out.push({
      id: item.id,
      label: item.skylarkId,
      copyId: item.skylarkId,
      sub: [item.description, item.serialNumber ? `Serial ${item.serialNumber}` : null].filter(Boolean).join(' · '),
      type: 'item',
      route: { name: 'warehouse-item', params: { trackingId: item.skylarkId } },
    })
    if (out.length >= 14) break
  }

  return out.slice(0, 10)
})

watch(results, () => { activeIndex.value = 0 })
watch(query, () => { activeIndex.value = 0 })

function navigate(result: Result) {
  router.push(result.route)
  emit('close')
}

function onKeydown(e: KeyboardEvent) {
  if (e.key === 'ArrowDown') {
    e.preventDefault()
    activeIndex.value = Math.min(activeIndex.value + 1, results.value.length - 1)
  } else if (e.key === 'ArrowUp') {
    e.preventDefault()
    activeIndex.value = Math.max(activeIndex.value - 1, 0)
  } else if (e.key === 'Enter') {
    const r = results.value[activeIndex.value]
    if (r) navigate(r)
  } else if (e.key === 'Escape') {
    emit('close')
  }
}

// Focus input when mounted
watch(inputRef, (el) => {
  if (el) nextTick(() => el.focus())
}, { immediate: true })
</script>

<template>
  <Teleport to="body">
    <div
      class="fixed inset-0 z-50 flex items-start justify-center pt-[15vh]"
      style="background: rgba(0,0,0,0.5); backdrop-filter: blur(4px);"
      @mousedown.self="emit('close')"
    >
      <div
        class="w-full max-w-lg rounded-xl overflow-hidden shadow-2xl"
        style="background: var(--surface-raised); border: 1px solid var(--surface-border);"
        @keydown="onKeydown"
      >
        <!-- Search input -->
        <div class="flex items-center gap-3 px-4 py-3" style="border-bottom: 1px solid var(--surface-border);">
          <Search :size="15" style="color: var(--ink-disabled);" class="shrink-0" />
          <input
            ref="inputRef"
            v-model="query"
            placeholder="Search projects, PO numbers, vendors, items, serials…"
            class="flex-1 bg-transparent text-sm outline-none"
            style="color: var(--ink-primary);"
          />
          <kbd class="text-2xs px-1.5 py-0.5 rounded font-mono shrink-0" style="background: var(--surface-border); color: var(--ink-tertiary);">ESC</kbd>
        </div>

        <!-- Results -->
        <div class="max-h-[360px] overflow-y-auto py-1">
          <div
            v-if="query && results.length === 0"
            class="px-4 py-8 text-center text-xs"
            style="color: var(--ink-disabled);"
          >
            No results for "{{ query }}"
          </div>

          <div v-else-if="!query" class="px-4 py-8 text-center text-xs" style="color: var(--ink-disabled);">
            Type to search projects, purchase orders and tracked items
          </div>

          <!-- div, not button: the row contains the CopyId button and buttons can't nest -->
          <div
            v-for="(result, i) in results"
            :key="result.id"
            type="button"
            class="w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors cursor-pointer"
            role="button"
            tabindex="-1"
            :style="i === activeIndex
              ? 'background: var(--surface-sunken);'
              : ''"
            @mouseenter="activeIndex = i"
            @click="navigate(result)"
          >
            <div
              class="w-7 h-7 rounded-md flex items-center justify-center shrink-0"
              style="background: var(--surface-sunken);"
            >
              <FolderKanban v-if="result.type === 'project'" :size="13" style="color: #74b743;" />
              <Tag v-else-if="result.type === 'item'" :size="13" style="color: rgb(34,211,238);" />
              <Package v-else :size="13" style="color: var(--ink-tertiary);" />
            </div>
            <div class="flex-1 min-w-0">
              <p class="text-xs font-medium truncate" style="color: var(--ink-primary);">
                <template v-if="result.copyId"><CopyId :value="result.copyId">{{ result.copyId }}</CopyId>{{ result.label.slice(result.copyId.length) }}</template>
                <template v-else>{{ result.label }}</template>
              </p>
              <p class="text-2xs truncate" style="color: var(--ink-tertiary);">{{ result.sub }}</p>
            </div>
            <kbd
              v-if="i === activeIndex"
              class="text-2xs px-1.5 py-0.5 rounded font-mono shrink-0"
              style="background: var(--surface-border); color: var(--ink-tertiary);"
            >↵</kbd>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>
